import React, { useEffect, useMemo, useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Package, CheckCircle2, Clock, AlertTriangle, RefreshCw, Layers, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { storeApi } from './storeApi';
import StoreLayout from './StoreLayout';
import OrderControlStatusCard from '../../components/OrderControlStatusCard';

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const startOfMonth = (off = 0) => { const d = new Date(); d.setMonth(d.getMonth() + off, 1); return d.toISOString().slice(0, 10); };
const endOfMonth = (off = 0) => { const d = new Date(); d.setMonth(d.getMonth() + off + 1, 0); return d.toISOString().slice(0, 10); };

const PRESETS = [
  { v: 'today', label: 'Today', range: () => ({ s: today(), e: today() }) },
  { v: 'week', label: 'This Week', range: () => ({ s: daysAgo(6), e: today() }) },
  { v: 'month', label: 'This Month', range: () => ({ s: startOfMonth(), e: endOfMonth() }) },
  { v: 'last_month', label: 'Last Month', range: () => ({ s: startOfMonth(-1), e: endOfMonth(-1) }) },
  { v: 'all', label: 'All Time', range: () => ({ s: '', e: '' }) },
];

// Brand orange palette for the composition donut.
const PIE = ['#10B981', '#F59E0B', '#A855F7', '#F97316'];

function Tile({ icon: Icon, label, value, tone = 'orange', sub }) {
  const cls = {
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  }[tone];
  return (
    <div className={`store-card p-4 border ${cls}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] uppercase tracking-wide font-semibold">{label}</p>
      </div>
      <p className="text-2xl font-bold" data-testid={`store-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
}

export default function StoreDashboard() {
  const [preset, setPreset] = useState('month');
  const [startDate, setStartDate] = useState(startOfMonth());
  const [endDate, setEndDate] = useState(endOfMonth());
  const [summary, setSummary] = useState(null);
  const [pendingSummary, setPendingSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const apply = (val) => {
    setPreset(val);
    const p = PRESETS.find((x) => x.v === val);
    if (p) { const r = p.range(); setStartDate(r.s); setEndDate(r.e); }
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    return p.toString();
  }, [startDate, endDate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Backend auto-scopes via the franchise_user JWT — store-portal users
      // ONLY see summary totals. SKU/product-level detail is intentionally
      // not exposed here per requirements.
      const [s, p] = await Promise.all([
        storeApi.get(`/franchise-performance/summary?${qs}`),
        // Pending procurement summary is independent of the date filter —
        // it always reflects the franchise's CURRENT open demand.
        storeApi.get('/store/pending-procurement-summary').catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setPendingSummary(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [qs]);

  const t = summary?.totals || {};
  const pieData = [
    { name: 'Invoiced', value: Number(t.invoiced_lines || 0) },
    { name: 'Pending', value: Number(t.pending_lines || 0) },
    { name: 'Partial', value: Number(t.partial_lines || 0) },
    { name: 'In-Flight', value: Number(t.in_flight_lines || 0) },
  ].filter((d) => d.value > 0);

  return (
    <StoreLayout>
      {/* Order acceptance state — visible to the franchise the moment they
          land. If blocked, the cart submit will fail with the same message. */}
      <div className="mb-5">
        <OrderControlStatusCard context="store" />
      </div>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="store-headline text-3xl">My Performance</h1>
          <p className="text-xs text-slate-500">Summary of your order fulfilment performance.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={preset} onChange={(e) => apply(e.target.value)} className="store-input w-[140px]" data-testid="store-date-preset">
            {PRESETS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          <button onClick={fetchAll} disabled={loading} className="store-btn-citrus text-sm px-3 py-1.5 flex items-center gap-1" data-testid="store-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}/> Refresh
          </button>
        </div>
      </div>

      {/* Pending Procurement Summary — live snapshot of what's waiting for
          stock. Helps the franchise see existing demand before they reorder. */}
      {pendingSummary && (pendingSummary.total_orders > 0 || pendingSummary.total_skus > 0) && (
        <div className="store-card p-4 border border-amber-200 bg-amber-50/60 mb-5"
          data-testid="store-pending-procurement-summary">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Pending Procurement Summary</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Stock already on the way for previous orders. Review before reordering the same SKUs.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="text-center bg-white/70 rounded p-2 border border-amber-100">
              <p className="text-2xl font-bold text-amber-900"
                data-testid="store-pending-orders">{pendingSummary.total_orders || 0}</p>
              <p className="text-[10px] uppercase tracking-wide text-amber-700">Open Orders</p>
            </div>
            <div className="text-center bg-white/70 rounded p-2 border border-amber-100">
              <p className="text-2xl font-bold text-amber-900"
                data-testid="store-pending-skus">{pendingSummary.total_skus || 0}</p>
              <p className="text-[10px] uppercase tracking-wide text-amber-700">Pending SKUs</p>
            </div>
            <div className="text-center bg-white/70 rounded p-2 border border-amber-100">
              <p className="text-2xl font-bold text-amber-900"
                data-testid="store-pending-qty">{Number(pendingSummary.total_qty || 0).toLocaleString('en-IN')}</p>
              <p className="text-[10px] uppercase tracking-wide text-amber-700">Pending Qty</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary KPI cards — SKU-line count based */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Tile icon={Package} label="Total Orders" value={Number(t.order_count || 0).toLocaleString()} tone="orange"/>
        <Tile icon={Layers} label="SKU Lines Ordered" value={Number(t.ordered_lines || 0).toLocaleString()} tone="orange"/>
        <Tile icon={CheckCircle2} label="SKU Lines Invoiced" value={Number(t.invoiced_lines || 0).toLocaleString()} tone="emerald"/>
        <Tile icon={Clock} label="SKU Lines Pending" value={Number(t.pending_lines || 0).toLocaleString()} tone="amber"/>
        <Tile icon={TrendingUp} label="Fulfillment" value={`${(t.fulfillment_pct || 0).toFixed(1)}%`} tone="emerald" sub="invoiced ÷ ordered lines"/>
        <Tile icon={AlertTriangle} label="Pending %" value={`${(t.pending_pct || 0).toFixed(1)}%`} tone="red" sub="pending ÷ ordered lines"/>
      </div>

      {/* Single composition donut — high-level only, no per-SKU breakdown */}
      <div className="store-card p-4 max-w-2xl mx-auto" data-testid="store-composition-card">
        <h3 className="font-semibold text-sm text-slate-700 mb-2">SKU-Line Composition</h3>
        {pieData.length === 0 ? (
          <p className="text-xs text-slate-400 py-10 text-center">No orders yet — start with <span className="text-orange-700 font-medium">Catalogue</span>.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={65} outerRadius={105} paddingAngle={2} label>
                {pieData.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]}/>)}
              </Pie>
              <Tooltip/>
              <Legend/>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </StoreLayout>
  );
}
